<script setup>
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useElementSize } from '@vueuse/core';
import BackButton from '../BackButton.vue';
import InboxName from '../InboxName.vue';
import MoreActions from './MoreActions.vue';
import Avatar from 'next/avatar/Avatar.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import wootConstants from 'dashboard/constants/globals';
import { conversationListPageURL } from 'dashboard/helper/URLHelper';
import { snoozedReopenTime } from 'dashboard/helper/snoozeHelpers';
import { useInbox } from 'dashboard/composables/useInbox';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  chat: {
    type: Object,
    default: () => ({}),
  },
  showBackButton: {
    type: Boolean,
    default: false,
  },
  // Quando true, em vez do BackButton (que navega via rota), renderiza
  // um botão ◀ que emite `back` — usado em overlays (kanban Sheet) onde
  // a "volta" é fechar o overlay, não navegar.
  closeable: {
    type: Boolean,
    default: false,
  },
  // Esconde só o dropdown "..." (Bloquear/Transcrição/Exportar PDF) —
  // mantém Resolver visível. Usado em overlays como o kanban Sheet.
  hideExtraActions: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['back']);

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const conversationHeader = ref(null);
const { width } = useElementSize(conversationHeader);
const { isAWebWidgetInbox } = useInbox();

const currentChat = computed(() => store.getters.getSelectedChat);
const accountId = computed(() => store.getters.getCurrentAccountId);

const chatMetadata = computed(() => props.chat.meta);

const backButtonUrl = computed(() => {
  const {
    params: { inbox_id: inboxId, label, teamId, id: customViewId },
    name,
  } = route;

  const conversationTypeMap = {
    conversation_through_mentions: 'mention',
    conversation_through_unattended: 'unattended',
  };
  return conversationListPageURL({
    accountId: accountId.value,
    inboxId,
    label,
    teamId,
    conversationType: conversationTypeMap[name],
    customViewId,
  });
});

const isHMACVerified = computed(() => {
  if (!isAWebWidgetInbox.value) {
    return true;
  }
  return chatMetadata.value.hmac_verified;
});

const currentContact = computed(() =>
  store.getters['contacts/getContact'](props.chat.meta.sender.id)
);

const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

// Webchat de continuidade: com sessão viva a resposta NÃO vai pro WhatsApp
// (vai pra tela do cliente). O selo diz isso e se ele está conectado agora.
const webchat = computed(() => props.chat.webchat || currentChat.value.webchat);

// O atendente pode mandar a resposta pelo WhatsApp sem fechar a tela do
// cliente (`reply_channel`). Aí o selo para de falar da presença dele: o que
// importa é que a próxima mensagem é PAGA.
const isReplyingOnWhatsapp = computed(
  () => webchat.value?.reply_channel === 'whatsapp'
);

// Link enviado e ainda não aceito (convite nunca aberto, ou reenvio que o
// cliente não tocou): ele está no WhatsApp, então a resposta vai pra lá
// (Webchat::Session.web_mode? só conta a sessão aceita).
const isInvitePending = computed(() => !!webchat.value?.link_pending);
// Pendente porque o cliente ABRIU o link e parou na confirmação de identidade
// (Webchat::Session#opened_unconfirmed?): a bolha do link diz "aberto", e o
// selo não pode dizer "ainda não abriu".
const isOpenedUnconfirmed = computed(() => !!webchat.value?.opened_unconfirmed);

// Presença AO VIVO do contato (presence.update a cada ~20s), a mesma do ponto
// do avatar. O payload da conversa trazia um retrato da hora do evento, e o
// selo seguia "online" depois de o cliente fechar a tela.
const isContactOnline = computed(
  () => currentContact.value?.availability_status === 'online'
);

const webchatLabel = computed(() => {
  if (!webchat.value) return '';
  if (isReplyingOnWhatsapp.value) {
    return t('CONVERSATION.WEBCHAT.REPLYING_ON_WHATSAPP');
  }
  if (isOpenedUnconfirmed.value) {
    return t('CONVERSATION.WEBCHAT.OPENED_UNCONFIRMED');
  }
  if (isInvitePending.value) {
    return t('CONVERSATION.WEBCHAT.INVITED');
  }
  return isContactOnline.value
    ? t('CONVERSATION.WEBCHAT.ONLINE')
    : t('CONVERSATION.WEBCHAT.OFFLINE');
});

// Cor = CANAL, não humor: verde é o WhatsApp (pago), azul é o webchat (de
// graça — `n-iris`, porque `n-blue` neste fork é a rampa LIMA da marca, ver
// scss/_next-colors.scss) — a mesma dupla do seletor no rodapé, pra o atendente ler os dois
// lugares com o mesmo código. A presença do cliente na tela continua dita
// pelo texto e pelo preenchimento: online é chapado, offline é só contorno.
const webchatBadgeClass = computed(() => {
  if (isReplyingOnWhatsapp.value || isInvitePending.value)
    return 'bg-n-green-3 text-n-green-12';
  return isContactOnline.value
    ? 'bg-n-iris-3 text-n-iris-12'
    : 'text-n-iris-11 ring-1 ring-n-iris-6';
});

const webchatTooltip = computed(() => {
  if (isReplyingOnWhatsapp.value)
    return t('CONVERSATION.WEBCHAT.REPLYING_ON_WHATSAPP_TOOLTIP');
  if (isOpenedUnconfirmed.value)
    return t('CONVERSATION.WEBCHAT.OPENED_UNCONFIRMED_TOOLTIP');
  if (isInvitePending.value) return t('CONVERSATION.WEBCHAT.INVITED_TOOLTIP');
  return t('CONVERSATION.WEBCHAT.TOOLTIP');
});

const snoozedDisplayText = computed(() => {
  const { snoozed_until: snoozedUntil } = currentChat.value;
  if (snoozedUntil) {
    return `${t('CONVERSATION.HEADER.SNOOZED_UNTIL')} ${snoozedReopenTime(snoozedUntil)}`;
  }
  return t('CONVERSATION.HEADER.SNOOZED_UNTIL_NEXT_REPLY');
});

const inbox = computed(() => {
  const { inbox_id: inboxId } = props.chat;
  return store.getters['inboxes/getInbox'](inboxId);
});

const hasMultipleInboxes = computed(
  () => store.getters['inboxes/getInboxes'].length > 1
);

const hasSlaPolicyId = computed(() => props.chat?.sla_policy_id);
</script>

<template>
  <div
    ref="conversationHeader"
    class="flex flex-col gap-3 items-center justify-between flex-1 w-full min-w-0 xl:flex-row px-3 py-2 border-b bg-n-background border-n-weak h-24 xl:h-12"
  >
    <div
      class="flex items-center justify-start w-full xl:w-auto max-w-full min-w-0 xl:flex-1"
    >
      <button
        v-if="closeable"
        type="button"
        class="size-8 grid place-items-center rounded-md hover:bg-n-alpha-2 text-n-slate-11 ltr:mr-2 rtl:ml-2"
        :aria-label="t('KANBAN.DEAL_DETAIL.HEADER.CLOSE')"
        @click="emit('back')"
      >
        <span class="i-lucide-arrow-left size-4" />
      </button>
      <BackButton
        v-else-if="showBackButton"
        :back-url="backButtonUrl"
        class="ltr:mr-2 rtl:ml-2"
      />
      <Avatar
        :name="currentContact.name"
        :src="currentContact.thumbnail"
        :size="32"
        :status="currentContact.availability_status"
        hide-offline-status
        rounded-full
      />
      <div
        class="flex flex-col items-start min-w-0 ml-2 overflow-hidden rtl:ml-0 rtl:mr-2"
      >
        <div class="flex flex-row items-center max-w-full gap-1 p-0 m-0">
          <span
            class="text-sm font-medium truncate leading-tight text-n-slate-12"
          >
            {{ currentContact.name }}
          </span>
          <fluent-icon
            v-if="!isHMACVerified"
            v-tooltip="$t('CONVERSATION.UNVERIFIED_SESSION')"
            size="14"
            class="text-n-amber-10 my-0 mx-0 min-w-[14px] flex-shrink-0"
            icon="warning"
          />
        </div>

        <div
          class="flex items-center gap-2 overflow-hidden text-xs conversation--header--actions text-ellipsis whitespace-nowrap"
        >
          <InboxName v-if="hasMultipleInboxes" :inbox="inbox" class="!mx-0" />
          <span v-if="isSnoozed" class="font-medium text-n-amber-10">
            {{ snoozedDisplayText }}
          </span>
          <span
            v-if="webchat"
            v-tooltip="webchatTooltip"
            class="inline-flex items-center min-w-0 gap-1 px-1.5 py-0.5 rounded-full text-[11px] font-medium"
            :class="webchatBadgeClass"
          >
            <span
              class="size-3"
              :class="
                isReplyingOnWhatsapp ? 'i-ph-whatsapp-logo' : 'i-lucide-link'
              "
            />
            <span class="truncate">{{ webchatLabel }}</span>
          </span>
        </div>
      </div>
    </div>
    <div
      class="flex flex-row items-center justify-start xl:justify-end flex-shrink-0 gap-2 w-full xl:w-auto header-actions-wrap"
    >
      <SLACardLabel
        v-if="hasSlaPolicyId"
        :chat="chat"
        show-extended-info
        :parent-width="width"
        class="hidden md:flex"
      />
      <MoreActions
        :conversation-id="currentChat.id"
        :hide-extra-actions="hideExtraActions"
      />
    </div>
  </div>
</template>
